import { useEffect } from "react";

export interface ProductCreatedEvent extends CustomEvent {
  detail: {
    product: unknown;
  };
}

export interface OfferCreatedEvent extends CustomEvent {
  detail: {
    offer: unknown;
  };
}

export type GlobalEventName = 
  | 'productCreated' 
  | 'offerCreated'
  | 'stripeConnected'
  | 'copy-link-success';

export const useGlobalEvents = () => {
  const dispatchProductCreated = (product: unknown) => {
    console.log('Dispatching product-created event with product:', product);
    const event = new CustomEvent("product-created", {
      detail: { product },
    });
    globalThis.dispatchEvent(event);
    console.log('Product-created event dispatched successfully');
  };

  const dispatchOfferCreated = (offer: unknown) => {
    const event = new CustomEvent("offer-created", {
      detail: { offer },
    });
    globalThis.dispatchEvent(event);
  };

  const dispatchStripeConnected = () => {
    const event = new CustomEvent("stripe-connected", {
      detail: { timestamp: Date.now() },
    });
    globalThis.dispatchEvent(event);
  };
  
  const dispatchCustomEvent = (eventName: string, detail = {}) => {
    const event = new CustomEvent(eventName, {
      detail: { ...detail, timestamp: Date.now() },
    });
    globalThis.dispatchEvent(event);
  };

  const on = (eventName: GlobalEventName, callback: Function) => {
    const eventMap = {
      productCreated: "product-created",
      offerCreated: "offer-created",
      stripeConnected: "stripe-connected"
    };
    
    const rawEventName = eventMap[eventName];
    
    const handler = (event: CustomEvent) => {
      callback(event.detail);
    };
    
    globalThis.addEventListener(rawEventName, handler as EventListener);
    
    return () => {
      globalThis.removeEventListener(rawEventName, handler as EventListener);
    };
  };

  return {
    dispatchProductCreated,
    dispatchOfferCreated,
    dispatchStripeConnected,
    dispatchCustomEvent,
    on
  };
};

export const useProductCreatedListener = (
  callback: (product: unknown) => void
) => {
  useEffect(() => {
    const handleProductCreated = (event: ProductCreatedEvent) => {
      callback(event.detail.product);
    };
    globalThis.addEventListener(
      "product-created",
      handleProductCreated as EventListener
    );
    return () => {
      globalThis.removeEventListener(
        "product-created",
        handleProductCreated as EventListener
      );
    };
  }, [callback]);
};

export const useOfferCreatedListener = (callback: (offer: unknown) => void) => {
  useEffect(() => {
    const handleOfferCreated = (event: OfferCreatedEvent) => {
      callback(event.detail.offer);
    };
    globalThis.addEventListener(
      "offer-created",
      handleOfferCreated as EventListener
    );
    return () => {
      globalThis.removeEventListener(
        "offer-created",
        handleOfferCreated as EventListener
      );
    };
  }, [callback]);
};
